// src/utils/api.js
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Longer timeout — Render free tier wakes up in 30-60 seconds
export const api = axios.create({
  baseURL: API_BASE,
  timeout: 60000, // 60 second timeout for cold start
});

export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

// ── Auto retry logic ─────────────────────────────────────────
// Retries failed requests up to 3 times with exponential backoff
const withRetry = async (fn, retries = 3, delay = 2000) => {
  for (let attempt = 1; attempt <= retries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      const isLast = attempt === retries;
      const shouldRetry =
        !err.response || // network error / server sleeping
        err.response.status >= 500 || // server error
        err.code === "ECONNABORTED"; // timeout

      if (isLast || !shouldRetry) throw err;

      // Wait before retry — longer each time
      await new Promise(r => setTimeout(r, delay * attempt));
    }
  }
};

// Random page — different images every refresh
const randomPage = (max = 20) => Math.floor(Math.random() * max) + 1;

// ── Pexels ───────────────────────────────────────────────────
export const fetchCurated = (page = null, per_page = 24, orientation = "") =>
  withRetry(() => api.get("/api/photos/curated", {
    params: { page: page || randomPage(15), per_page, orientation: orientation || undefined },
  }));

export const searchPexels = (query, page = 1, per_page = 24, orientation = "") =>
  withRetry(() => api.get("/api/photos/search", {
    params: { query, page, per_page, orientation: orientation || undefined },
  }));

// ── Wallhaven ────────────────────────────────────────────────
export const fetchWallhaven = (page = null, ratio = "") =>
  withRetry(() => api.get("/api/wallhaven/curated", {
    params: { page: page || randomPage(10), ratio: ratio || undefined },
  }));

export const searchWallhaven = (query, page = 1, ratio = "") =>
  withRetry(() => api.get("/api/wallhaven/search", {
    params: { query, page, ratio: ratio || undefined },
  }));

// ── Combined ─────────────────────────────────────────────────
export const fetchAll = (page = null, orientation = "") =>
  withRetry(() => api.get("/api/all/curated", {
    params: { page: page || randomPage(10), orientation: orientation || undefined },
  }));

export const searchAll = (query, page = 1, orientation = "") =>
  withRetry(() => api.get("/api/all/search", {
    params: { query, page, orientation: orientation || undefined },
  }));

// ── Downloads ────────────────────────────────────────────────
export const trackDownload = (payload) =>
  api.post("/api/downloads/track", payload);

export const fetchHistory = () =>
  withRetry(() => api.get("/api/downloads/history"));
